import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import {
  catchError,
  finalize,
  map,
  switchMap,
  takeUntil,
} from "rxjs/operators";
import { HttpErrorResponse } from "@angular/common/http";
import { AppLoadError, ICountry } from "../shared/models";
import { CountryListService } from "../shared/services/api/country-list.service";

@Injectable({
  providedIn: "root",
})
export class CountryListStoreService implements OnDestroy {
  private destroyed$ = new Subject<void>();

  // State management subjects
  private _countryList = new BehaviorSubject<ICountry[]>([]);
  private _error = new BehaviorSubject<AppLoadError | null>(null);
  private _loading = new BehaviorSubject<boolean>(false);

  // State observables
  countryList$ = this._countryList.asObservable();
  error$ = this._error.asObservable();
  loading$ = this._loading.asObservable();

  // get current value

  get countryList() {
    return this._countryList.getValue();
  }

  constructor(private service: CountryListService) {}

  selectCountryList(): Observable<ICountry[]> {
    return this.countryList$.pipe(map((countryList) => countryList));
  }

  // Select the loading state
  selectLoading(): Observable<boolean> {
    return this.loading$.pipe(map((loading) => loading));
  }

  // Select the error state
  selectError(): Observable<AppLoadError | null> {
    return this.error$.pipe(map((error) => error));
  }

  /**
   * Fetches the country list, updating state accordingly.
   * @GET
   */
  loadCountryList(): Observable<ICountry[]> {
    const stored = this.countryList;
    if (stored.length !== 0) {
      // If data is already stored, emit it directly
      this._countryList.next(stored);
      return of(stored);
    } else {
      // Set loading state and fetch data
      this._loading.next(true);
      return this.service.getCountryList().pipe(
        takeUntil(this.destroyed$),
        switchMap((CountryList) => {
          this._countryList.next(CountryList);

          return of(CountryList);
        }),
        catchError((err) => {
          const error = err instanceof HttpErrorResponse ? err.error : err;
          const displayError = error
            ? new AppLoadError(error.id, error.message)
            : new AppLoadError();

          this._error.next(displayError);
          return of(null);
        }),
        finalize(() => {
          // Reset loading state
          this._loading.next(false);
        })
      );
    }
  }

  /**
   * Cleanup resources on destruction.
   */
  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
